import { ANALYTICS_EVENTS, MARKET, PAYMENT_TYPES, POINTS_PER_DOLLAR } from '@nandosaus/constants';
import { useRootStore } from '@nandosaus/state-management';
import { get } from 'lodash';
import { observer } from 'mobx-react';
import { useRouter } from 'next/router';
import PropTypes from 'prop-types';
import { useEffect, useState } from 'react';
import styled from 'styled-components';

import { analytics } from '@analytics';
import { Divider } from '@components/divider';
import { Button } from '@components/button/new';
import { DefaultState } from '@components/default';
import { ErrorBoundary } from '@components/error-boundary';
import { ExpressCheckoutModal } from '@components/express-checkout-modal';

import { Box, Flex } from '@components/grid';
import { CartPointsRedemption } from '@components/cart-points-redemption';
import { GroupedItemList } from '@components/grouped-item-list';
import { Icon } from '@components/icons';
import { Link } from '@components/link';
import { PromotionList } from '@components/offer/promotion-list';
import { FreeGiftOrderItem } from '@components/free-gift-order-item';
import { OrderItemList } from '@components/order-item-list';
import { OrderSettingsSummary } from '@components/order-settings-summary';
import { OrderTotals } from '@components/order-totals';
import { P, H4, Label } from '@components/typography/new';
import { UpsellModal } from '@components/upsell-modal';
import { colors, navigation } from '@constants';
import { useBrazeUpsellContent } from '@hooks/use-braze-upsell-content';
import { useFeature } from '@hooks/use-feature';
import { useFreeGiftOrderItem } from '@hooks/use-free-gift-order-item';
import { useUpsellModalHandler } from '@hooks/use-upsell-modal-handler';
import { createNavId } from '@utils/createNavIds';
import { formatSubCartsForProducts } from '@nandosaus/state-management/lib/util/analytics';
import { priceFormatter } from '@nandosaus/state-management/lib/util/formatter';
import { withFreeGiftOrderItem } from '@nandosaus/state-management/lib/util/free-gift-order-item';
import { ModalFloatingHeader } from '@components/modal/new/modal-header';
import { useMarket } from '../../hooks/use-market';
import { clearPersistedCartData } from '../../utils/data';
import { ConfirmButton } from '../confirm-button';
import { LocationDistanceAlert } from '../location-distance-alert';

import { SurpriseAndDelight } from '../surprise-and-delight';
import { CartAlert } from './cart-alert';
import { DeliveryDiscountCta } from './delivery-discount-cta';

const StyledLink = styled(Link)`
  text-decoration: underline;
`;

const Overflow = styled(Flex)`
  overflow-y: auto;
`;

const FooterPadding = styled(Flex)`
  padding-bottom: max(24px, env(safe-area-inset-bottom));
`;

// Bottom padding for the sticky checkout footer. `env(safe-area-inset-bottom)` resolves to 0 in
// mobile Safari (its own toolbar occupies the home-indicator strip, so iOS reports no inset), which
// left the CTA sitting flush against the browser chrome. The 32px floor keeps a visible gap there,
// while the env() term still wins when the site runs full-screen from the home screen.
const STICKY_FOOTER_PADDING_BOTTOM = 'max(32px, env(safe-area-inset-bottom))';

const CartPreviewContainer = ({ hidePreview, cartPreviewHeading, children, ...rest }) => {
  return (
    <Flex {...rest} flexDirection="column">
      <ErrorBoundary>
        <Flex mx="16px">
          <ModalFloatingHeader isAbsolute={false} handleClose={hidePreview} backgroundColor="warmWhite100">
            <Flex flexDirection="column" pt="16px" pb="16px" zIndex={40}>
              <H4 my="0" textAlign="center">
                {cartPreviewHeading}
              </H4>
            </Flex>
          </ModalFloatingHeader>
        </Flex>

        <Flex height="100%" mx={{ xs: '16px', md: '32px' }} flexDirection="column">
          {children}
        </Flex>
      </ErrorBoundary>
    </Flex>
  );
};

CartPreviewContainer.propTypes = {
  hidePreview: PropTypes.bool.isRequired,
  cartPreviewHeading: PropTypes.string.isRequired,
  children: PropTypes.node.isRequired,
};

const CartPreview = ({ hidePreview, onSettingsClick, enableUpsellModal, onWelcomePage }) => {
  const {
    CartStore,
    MemberStore,
    MenuStore,
    OrderStore,
    OrderingContextStore,
    DeliveryStore,
    GroupOrderStore,
    PastOrdersStore,
    PaymentStore,
  } = useRootStore();
  const router = useRouter();
  const market = useMarket();

  const deliveryDisclaimerEnabled = useFeature('delivery-disclaimer');
  const showNutritionDisclaimer = market === MARKET.AUSTRALIA;
  const cartRemovalEnabled = useFeature('remove-from-cart');
  const partialPointRedemptionEnabled = useFeature('partial-point-redemption-enabled');
  const expressCheckoutEnabled = useFeature('express-checkout');
  const [isExpressCheckoutOpen, setIsExpressCheckoutOpen] = useState(false);

  // Signed-out users get the express checkout sheet (sign in / wallet / guest) instead of going
  // straight to the checkout page. Group and catering orders keep the standard checkout flow.
  const shouldOfferExpressCheckout =
    expressCheckoutEnabled &&
    !MemberStore.isSignedIn &&
    !GroupOrderStore.isInGroup &&
    !OrderingContextStore.isCateringOrder;

  // Drives the new points-redemption layout/styling. Gated only on the flag (not sign-in or edit mode)
  // so the new look shows for everyone with the flag on, including signed-out users and while editing.
  const showPeriPointsToggle = partialPointRedemptionEnabled;

  const deliveryCost = OrderStore.formattedDeliveryCost;

  // The redemption row renders for everyone with the flag on: signed-in members see the interactive
  // toggle/editor, signed-out users see a "Sign in to redeem your PERi-Perks" prompt (handled inside
  // PointsRedemptionToggleRow, mirroring mobile). When editing an existing (catering) order we don't
  // allow points to be added or changed: if points are already applied we still show the redemption
  // row, but read-only; if none are applied we hide the redemption component (keeping the layout).
  const pointsAlreadyApplied = CartStore.hasPaymentType(PAYMENT_TYPES.LOYALTY) && CartStore.loyaltyPointsToRedeem > 0;
  const periPointsReadOnly = CartStore.isEditingOrder;
  // Derive the toggle from the store so it survives the cart modal unmounting (closing + reopening
  // the cart drawer would otherwise reset the toggle to OFF while the store still held LOYALTY +
  // the chosen amount, leaving the UI desynced from the totals line).
  // When points payment is disabled (promo / free delivery / public holiday) the toggle row shows
  // the reason inline instead of an applied amount, so keep the totals in step by treating points
  // as off — otherwise the cart drawer (which has no auto-removal effect) would still deduct them.
  const effectiveToggleOn = CartStore.isEditingOrder
    ? pointsAlreadyApplied
    : CartStore.hasPaymentType(PAYMENT_TYPES.LOYALTY) && !CartStore.pointsPaymentDisabled;
  const showPointsRedemption = showPeriPointsToggle && (!CartStore.isEditingOrder || pointsAlreadyApplied);

  const orderTotalDollars = (CartStore.totalPrices?.cents ?? 0) / 100;
  const availablePointsInDollars = parseFloat(MemberStore.profile?.displayPointsInDollars ?? '0') || 0;
  const maxPointsAmountDollars = Math.min(availablePointsInDollars, orderTotalDollars);

  const handlePointsToggle = checked => {
    if (checked) {
      CartStore.setSplitPaymentType(PAYMENT_TYPES.LOYALTY);
      CartStore.setLoyaltyPointsToRedeem(maxPointsAmountDollars);
    } else {
      CartStore.removeSplitPaymentType(PAYMENT_TYPES.LOYALTY);
    }
  };

  const handlePointsAmountChange = value => {
    CartStore.setLoyaltyPointsToRedeem(Math.min(value, maxPointsAmountDollars));
  };

  // While editing a catering order, points can't be added/changed and startEditOrder doesn't
  // re-apply LOYALTY to the live cart — so the live cart reports 0 points. Surface the points spent
  // on the ORIGINAL order instead (read-only) so the redeemed amount stays visible during editing.
  const editedCateringOrder = CartStore.isEditingOrder
    ? PastOrdersStore.getCateringOrderById(CartStore.orderUuid)
    : null;
  const liveLoyaltyCents = effectiveToggleOn ? CartStore.splitAmountLoyalty : 0;
  const livePointsSpent = effectiveToggleOn ? CartStore.loyaltyPointsToRedeem * POINTS_PER_DOLLAR : 0;
  // Clamp the original order's redeemed amount to the (re-editable) live total so the points line
  // never exceeds what's payable and Subtotal − points = Total stays consistent as items change.
  const loyaltyAppliedCents = editedCateringOrder
    ? Math.min(editedCateringOrder.loyaltyAmountCents, OrderStore.payableTotal.cents)
    : liveLoyaltyCents;
  const pointsSpent = editedCateringOrder ? (loyaltyAppliedCents / 100) * POINTS_PER_DOLLAR : livePointsSpent;
  const payableAfterPointsCents = Math.max(0, OrderStore.payableTotal.cents - loyaltyAppliedCents);
  const payableAfterPoints = {
    cents: payableAfterPointsCents,
    points: OrderStore.payableTotal.points,
    formattedPrice: priceFormatter(payableAfterPointsCents),
  };

  const emptyCart = CartStore.isEmpty && !CartStore.anythingToUndo;

  const {
    content: upsellContent,
    recommendedProducts,
    newProductIds,
    brazeLogImpression,
    brazeLogClick,
  } = useBrazeUpsellContent();

  const {
    isUpsellModalEnabled,
    isUpsellModalOpen,
    hasUpsellModalShown,
    closeUpsellModal,
    openUpsellModal,
  } = useUpsellModalHandler();

  useEffect(() => {
    analytics.track(ANALYTICS_EVENTS.CART_VIEWED, {
      products: formatSubCartsForProducts(CartStore.subCarts, {
        orderType: CartStore.orderType,
        group: GroupOrderStore.group,
      }),
      total: CartStore.totalPrices.dollarValue,
    });
  }, [CartStore, GroupOrderStore]);

  // Prefetch which wallets the restaurant and device support while the customer reviews their
  // cart, so the express checkout sheet's Apple Pay / Google Pay buttons don't pop in after it opens.
  useEffect(() => {
    if (shouldOfferExpressCheckout && CartStore.restaurantId) {
      PaymentStore.loadPaymentMethods({ restaurantId: CartStore.restaurantId });
    }
  }, [shouldOfferExpressCheckout, CartStore.restaurantId, PaymentStore]);

  const isShowingBrowseMenuButton = router.pathname !== navigation.MENU && router.pathname !== navigation.CATERING;

  // S&D free gift as a removable synthetic order item (trash button clears the
  // selection) — composed into the order list below.
  const freeGiftItem = useFreeGiftOrderItem({ removable: true });

  // Progress bar "Browse menu" CTA: close the cart and head to the menu so the
  // customer can add items toward the S&D threshold (the catering menu for catering
  // pickup / delivery). Already on that menu, just close the cart overlay.
  const handleBrowseMenu = () => {
    hidePreview();
    const menuPath =
      OrderingContextStore.isCatering || OrderingContextStore.isCateringDelivery
        ? navigation.CATERING
        : navigation.MENU;
    if (router.pathname !== menuPath) {
      router.push(menuPath);
    }
  };

  const getActionButtonTitle = () => {
    if (MenuStore.loading) {
      return 'Loading menu';
    }
    if (DeliveryStore.loading) {
      return 'Estimating delivery';
    }
    if (CartStore.loading) {
      return 'Validating order';
    }
    if (CartStore.isEditingOrder) {
      return 'Confirm order update';
    }
    if (isShowingBrowseMenuButton) {
      return 'Continue';
    }

    return 'Continue to checkout';
  };

  const onCheckoutClick = async () => {
    if (onWelcomePage) {
      analytics.track(ANALYTICS_EVENTS.WELCOME_PAGE_CHECKOUT_CLICKED);
    }

    if (enableUpsellModal && isUpsellModalEnabled && !hasUpsellModalShown && !CartStore.isEditingOrder) {
      openUpsellModal();
    } else {
      // CartStore is handling errors
      try {
        const { error } = await CartStore.validateOrder();
        if (error) {
          return;
        }
      } catch (err) {
        return;
      }

      if (CartStore.isEditingOrder) {
        // Get order UUID from CartStore
        const { submitted } = await CartStore.updateCateringOrder(CartStore.orderUuid);

        if (submitted) {
          clearPersistedCartData();
          OrderStore.clearSessionData();

          // Navigate back to catering orders page with success parameter
          router.push('/account/catering-orders?success=true');
        } else {
          // On error, redirect to checkout with current query params
          router.push({ pathname: navigation.CHECKOUT, query: router.query });
        }

        return;
      }

      if (shouldOfferExpressCheckout) {
        setIsExpressCheckoutOpen(true);
        return;
      }

      router.push(navigation.CHECKOUT);
    }
  };

  const shortestDeliveryEstimate = DeliveryStore.shortestDeliveryTime;
  const isDiscountActive = shortestDeliveryEstimate?.isDiscountActive;
  const isDiscountApplied = shortestDeliveryEstimate?.isDiscountApplied;
  const deliverySponsor = shortestDeliveryEstimate?.sponsor;

  let cartPreviewHeading = 'Your order';
  if (GroupOrderStore.isInGroup) {
    cartPreviewHeading = GroupOrderStore.name;
  }
  if (CartStore.isEditingOrder) {
    cartPreviewHeading = 'Edit order';
  }

  if (cartRemovalEnabled && CartStore.isCartRemoved) {
    return (
      <CartPreviewContainer height="100%" hidePreview={hidePreview} cartPreviewHeading={cartPreviewHeading}>
        <DefaultState
          title="All items have been removed from your order"
          subtitle="You can undo this change and get your items back."
          onUndo={CartStore.undoRemoveAllSubCarts}
        />
      </CartPreviewContainer>
    );
  }

  if (emptyCart) {
    return (
      <CartPreviewContainer height="100%" hidePreview={hidePreview} cartPreviewHeading={cartPreviewHeading}>
        <DefaultState title="You haven’t added anything to your cart!" subtitle="Items you add will appear here" />
      </CartPreviewContainer>
    );
  }

  return (
    <Flex
      flexDirection="column"
      justifyContent="space-between"
      min-height="100%"
      height="100vh"
      overflow="hidden"
      role="region"
      aria-label="Cart Preview"
    >
      <ErrorBoundary>
        <Overflow flex={1} position="relative" flexDirection="column">
          <CartPreviewContainer hidePreview={hidePreview} cartPreviewHeading={cartPreviewHeading} />

          <Flex px={{ xs: '16px', md: '32px' }} position="relative" flexDirection="column">
            <Flex pt={{ xs: '12px', md: '25px' }} />

            <CartAlert
              errors={OrderStore.allErrors}
              hasNetworkError={CartStore.hasNetworkError}
              restaurant={OrderStore.restaurant}
              isSignedIn={MemberStore.isSignedIn}
              isGuestOrderingAvailable={CartStore.isGuestOrderingAvailable}
              mx="0"
              mb="4px"
            />

            <Flex pb="4px">
              <OrderSettingsSummary
                isCompact
                isModal
                onClick={() => {
                  analytics.track(ANALYTICS_EVENTS.ORDER_SETTINGS_OPENED, { location: 'Banner' });
                  onSettingsClick();
                }}
              />
            </Flex>

            <Flex pb="4px">
              <LocationDistanceAlert />
            </Flex>

            <Flex
              mb={{ xs: '28px', md: '48px' }}
              py="20px"
              flexDirection="column"
              backgroundColor={colors.white}
              borderRadius="8px"
              px={{ xs: '20px', md: '24px' }}
            >
              {/* S&D slot nested at the top of the order-items list (Figma). The
                  slot owns its own bottom spacing and renders null in several
                  states, so there's no call-site wrapper/padding (gating that on
                  campaignId here left a phantom gap when the slot returned null). */}
              {/* Hidden while editing a catering order: the edit keeps the reward saved at checkout. */}
              {!CartStore.isEditingOrder && <SurpriseAndDelight onBrowseMenu={handleBrowseMenu} />}

              {GroupOrderStore.isInGroup && GroupOrderStore.isHost && (
                <Flex py="16px">
                  <P variant="small">You’re paying for this group order</P>
                </Flex>
              )}
              <Flex flexDirection="column">
                {/* S&D free gift: composed into the order list at the canonical
                    position (withFreeGiftOrderItem). Group hosts render multiple
                    sub-cart lists, so the gift gets a standalone row above them. */}
                {GroupOrderStore.isHost ? (
                  <>
                    <FreeGiftOrderItem />
                    <GroupedItemList subCarts={CartStore.subCartsAsArray} />
                  </>
                ) : (
                  <OrderItemList orderItems={withFreeGiftOrderItem(CartStore.orderItems, freeGiftItem)} />
                )}

                <Divider width="100%" />

                {cartRemovalEnabled && (
                  <Flex width="100%" justifyContent="center" pt="20px">
                    <Flex
                      gap="12px"
                      alignItems="center"
                      as="button"
                      onClick={CartStore.removeAllSubCarts}
                      disabled={CartStore.loading}
                    >
                      <Icon name="trash" width="24px" height="24px" fill={colors.primaryRed} />
                      <Label textDecoration="underline" color="black" fontWeight="bold" textTransform="none">
                        Clear Order
                      </Label>
                    </Flex>
                  </Flex>
                )}

                {deliveryDisclaimerEnabled && OrderStore.isDelivery && (
                  <P variant="small" color="greyPrimary">
                    Our menu items cost more for Delivery. Dine-in with us or stop by for a cheeky takeaway to dodge
                    these extra costs.
                  </P>
                )}
              </Flex>
            </Flex>
            <DeliveryDiscountCta
              isDelivery={OrderingContextStore.isDelivery}
              isDiscountActive={isDiscountActive}
              isDiscountApplied={isDiscountApplied}
              deliverySponsor={deliverySponsor}
              onClick={() => {
                const { categoryNames } = MenuStore;
                const firstCategoryId = createNavId(get(categoryNames, '[0]', null));
                const url = firstCategoryId ? `${navigation.MENU}${firstCategoryId}` : navigation.MENU;

                hidePreview();

                analytics.track(ANALYTICS_EVENTS.PROMOTION_CLICKED, {
                  name: 'Discounted Delivery',
                  location: 'Cart',
                });

                router.push(url);
              }}
            />
          </Flex>
          {!CartStore.isEditingOrder && !GroupOrderStore.isGroupInvitee && (
            <PromotionList
              mb={{ xs: '24px', md: '32px' }}
              mx="0"
              analyticsLocation="Cart Preview"
              isCheckoutOfferVariant
            />
          )}
          <Flex px={{ xs: '16px', md: '32px' }} position="relative" flexDirection="column">
            <Box display="flex" flexDirection="column" gap={{ xs: '24px', md: '32px' }} mb={{ xs: '24px', md: '32px' }}>
              {GroupOrderStore.isGroupInvitee && (
                <P color="grey70" variant="small" textAlign="center" fontWeight="bold">
                  PERi-Perks offers aren&apos;t redeemable on group orders
                </P>
              )}
              {showPeriPointsToggle ? (
                <OrderTotals
                  deliveryCost={deliveryCost}
                  discountPrices={CartStore.discountPrices}
                  subtotalPrices={CartStore.subtotalPrices}
                  totalPrices={payableAfterPoints}
                  surcharges={CartStore.surcharges}
                  showDisclaimer={false}
                  isDiscountApplied={isDiscountApplied}
                  deliverySponsor={deliverySponsor}
                  py="0"
                  pointsAreLineItem={false}
                  pointsSpent={pointsSpent}
                  pointsAmountFormatted={priceFormatter(loyaltyAppliedCents)}
                />
              ) : (
                <OrderTotals
                  deliveryCost={deliveryCost}
                  showDisclaimer={false}
                  discountPrices={CartStore.discountPrices}
                  subtotalPrices={CartStore.subtotalPrices}
                  totalPrices={OrderStore.payableTotal}
                  surcharges={CartStore.surcharges}
                  isDiscountApplied={isDiscountApplied}
                  deliverySponsor={deliverySponsor}
                  py="0"
                />
              )}

              {isDiscountActive && OrderingContextStore.isDelivery && (
                <P color="grey70" variant="small" textAlign="center">
                  Free Delivery {deliverySponsor ? `by ${deliverySponsor.name}` : ''} is subjected to the following{' '}
                  <StyledLink target="_blank" variant="small" href={navigation.TERMS_CONDITIONS}>
                    terms and conditions
                  </StyledLink>
                </P>
              )}
              {GroupOrderStore.isGroupInvitee && (
                <P color="grey70" variant="small" textAlign="center" fontWeight="bold">
                  Only the person paying for the order will earn PERi-PERKS
                </P>
              )}
              {MenuStore.menuType === 'catering' && (
                <Flex flexDirection="column" gap="16px">
                  <Box pt="1px" width="100%" backgroundColor="warmWhite3" />
                  <Flex flexDirection="column">
                    <Flex justifyContent="space-between">
                      <P variant="medium-semi-bold">Per person</P>
                      {!!OrderingContextStore.cateringHeadcount && (
                        <P variant="medium">{CartStore.formattedPricePerPerson}</P>
                      )}
                    </Flex>
                    <Flex>
                      <P variant="extra-small">
                        Based on {OrderingContextStore.cateringHeadcount}{' '}
                        {OrderingContextStore.cateringHeadcount > 1 ? 'people' : 'person'}
                      </P>
                      <P
                        ml="4px"
                        variant="extra-small-semi-bold"
                        color="black"
                        as="button"
                        textTransform="none"
                        textDecoration="underline"
                        onClick={() => {
                          onSettingsClick();
                        }}
                      >
                        Edit
                      </P>
                    </Flex>
                  </Flex>
                  <Box pt="1px" width="100%" backgroundColor="warmWhite3" />
                  {MenuStore.menuType === 'catering' && OrderStore.restaurant?.minimumCateringOrderSpend && (
                    <P variant="standard-bold">
                      Minimum ${OrderStore.restaurant.minimumCateringOrderSpend} for catering orders
                    </P>
                  )}
                </Flex>
              )}
              {showNutritionDisclaimer && (
                <P variant="small">
                  Kilojoule calculations do not include drinks or customisations to menu items. The average adult daily
                  energy intake is 8,700 kJ.
                </P>
              )}
            </Box>
          </Flex>

          {/* footer */}
          {showPeriPointsToggle && (
            <Box
              data-testid="cart-checkout-footer"
              position="sticky"
              width="100%"
              bottom="0"
              display="flex"
              flexDirection="column"
              gap="12px"
              padding="16px"
              zIndex="50"
              backgroundColor={colors.warmWhite3}
              pb={STICKY_FOOTER_PADDING_BOTTOM}
            >
              {showPointsRedemption && (
                <CartPointsRedemption
                  availablePointsDollars={maxPointsAmountDollars.toFixed(2)}
                  pointsToggleChecked={effectiveToggleOn}
                  onPointsToggleChange={handlePointsToggle}
                  pointsAmountDollars={CartStore.loyaltyPointsToRedeem}
                  maxPointsAmountDollars={maxPointsAmountDollars}
                  onPointsAmountChange={handlePointsAmountChange}
                  readOnly={periPointsReadOnly}
                />
              )}
              <ConfirmButton
                disabled={!CartStore.allowCheckout}
                role="button"
                onClick={onCheckoutClick}
                buttonText={getActionButtonTitle()}
                price={payableAfterPoints.formattedPrice}
                kilojoules={OrderStore.kilojoulesTotal}
                padding="0"
              />

              {(isShowingBrowseMenuButton || CartStore.isEditingOrder) && (
                <Button
                  role="button"
                  px={0}
                  mb="0"
                  variant="tertiary"
                  size="tall"
                  widthType="full"
                  onClick={() => {
                    if (CartStore.isEditingOrder) {
                      hidePreview();
                      return;
                    }
                    router.push(navigation.MENU);
                  }}
                >
                  Browse the Menu
                </Button>
              )}
              {CartStore.isEditingOrder && (
                <P
                  as="button"
                  onClick={() => {
                    hidePreview();
                    OrderStore.clearSessionData();
                    router.push(navigation.CATERING);
                  }}
                  variant="small-semi-bold"
                  textDecoration="underline"
                  width="100%"
                >
                  Cancel editing
                </P>
              )}
            </Box>
          )}

          {!showPeriPointsToggle && (
            <>
              <Box
                data-testid="cart-checkout-footer"
                position="sticky"
                zIndex="50"
                width="100%"
                bottom="0"
                pb={STICKY_FOOTER_PADDING_BOTTOM}
                px={{ xs: '16px', md: '32px' }}
              >
                <ConfirmButton
                  disabled={!CartStore.allowCheckout}
                  role="button"
                  onClick={onCheckoutClick}
                  buttonText={getActionButtonTitle()}
                  price={OrderStore.payableTotal.formattedPrice}
                  kilojoules={OrderStore.kilojoulesTotal}
                  padding="0"
                />
              </Box>

              <Flex flexDirection="column" px={{ xs: '16px', md: '32px' }}>
                {(isShowingBrowseMenuButton || CartStore.isEditingOrder) && (
                  <Button
                    role="button"
                    px={0}
                    mb="12px"
                    variant="tertiary"
                    size="tall"
                    widthType="full"
                    onClick={() => {
                      if (CartStore.isEditingOrder) {
                        hidePreview();
                        return;
                      }
                      router.push(navigation.MENU);
                    }}
                  >
                    Browse the Menu
                  </Button>
                )}
                {CartStore.isEditingOrder && (
                  <P
                    as="button"
                    onClick={() => {
                      hidePreview();
                      OrderStore.clearSessionData();
                      router.push(navigation.CATERING);
                    }}
                    my="20px"
                    variant="small-semi-bold"
                    textDecoration="underline"
                    width="100%"
                  >
                    Cancel editing
                  </P>
                )}
              </Flex>
            </>
          )}
          {!showPeriPointsToggle && <FooterPadding />}
        </Overflow>
      </ErrorBoundary>

      {enableUpsellModal && !hasUpsellModalShown && (
        <UpsellModal
          modalOpen={isUpsellModalOpen}
          handleModalClose={closeUpsellModal}
          recommendedProducts={recommendedProducts}
          newProductIds={newProductIds}
          content={upsellContent}
          brazeLogImpression={brazeLogImpression}
          brazeLogClick={brazeLogClick}
        />
      )}

      {isExpressCheckoutOpen && <ExpressCheckoutModal open onClose={() => setIsExpressCheckoutOpen(false)} />}
    </Flex>
  );
};

CartPreview.propTypes = {
  hidePreview: PropTypes.func.isRequired,
  onSettingsClick: PropTypes.func.isRequired,
  enableUpsellModal: PropTypes.bool,
  onWelcomePage: PropTypes.bool,
};

CartPreview.defaultProps = {
  enableUpsellModal: false,
  onWelcomePage: false,
};

const ComposedCartPreview = observer(CartPreview);
export { ComposedCartPreview as CartPreview };
